import DateTimeDisplay from '@app/components/common/DateTimeDisplay';
import { DEFAULT_CURRENCY } from '@app/common/constants';
import { currencyFormatter } from '@app/utils/pricingCurrencyUtils';
import type { BaseColumn } from '@konnecta/generic-table';
import { Tag, Typography } from 'antd';
import { TFunction } from 'i18next';
import type { InventoryMovementRow, InventoryMovementType, RentalRateUnit } from '../../types';

/** Translated label for a movement type. */
export const getMovementTypeLabel = (movementType: InventoryMovementType, t: TFunction): string => {
  switch (movementType) {
    case 'purchase':
      return t('inventory-mtype-purchase');
    case 'sale':
      return t('inventory-mtype-sale');
    case 'give':
      return t('inventory-mtype-give');
    case 'rental_out':
      return t('inventory-mtype-rental_out');
    case 'rental_return':
      return t('inventory-mtype-rental_return');
    case 'adjustment':
      return t('inventory-mtype-adjustment');
    default:
      return movementType;
  }
};

/** Antd Tag color per movement type. */
const getMovementTypeColor = (movementType: InventoryMovementType): string => {
  switch (movementType) {
    case 'purchase':
      return 'green';
    case 'sale':
      return 'blue';
    case 'give':
      return 'gold';
    case 'rental_out':
      return 'geekblue';
    case 'rental_return':
      return 'cyan';
    case 'adjustment':
      return 'default';
    default:
      return 'default';
  }
};

const getRentalRateUnitLabel = (unit: RentalRateUnit, t: TFunction): string => {
  switch (unit) {
    case 'day':
      return t('inventory-rental-rate-unit-day');
    case 'week':
      return t('inventory-rental-rate-unit-week');
    case 'month':
      return t('inventory-rental-rate-unit-month');
    default:
      return unit;
  }
};

/** The price relevant to a given movement type (cost / sale price / rental rate). */
const renderPrice = (row: InventoryMovementRow, locale: string, t: TFunction) => {
  if (row.movementType === 'purchase' && row.unitCost != null) {
    return currencyFormatter(row.unitCost, DEFAULT_CURRENCY, locale);
  }
  if (row.movementType === 'sale' && row.unitPrice != null) {
    return currencyFormatter(row.unitPrice, DEFAULT_CURRENCY, locale);
  }
  if (row.movementType === 'rental_out' && row.rentalRate != null) {
    const rate = currencyFormatter(row.rentalRate, DEFAULT_CURRENCY, locale);
    return row.rentalRateUnit ? `${rate} / ${getRentalRateUnitLabel(row.rentalRateUnit, t)}` : rate;
  }
  return '-';
};

const renderRecipient = (row: InventoryMovementRow, t: TFunction): string => {
  if (!row.recipientType) return '-';
  const who = row.recipientName || (row.recipientId != null ? `#${row.recipientId}` : '');
  const typeLabel = t(`inventory-recipient-type-${row.recipientType}`);
  return who ? `${typeLabel}: ${who}` : typeLabel;
};

export const getInventoryMovementsTableColumns = (t: TFunction, locale: string): BaseColumn<InventoryMovementRow>[] => [
  {
    id: 'occurredAt',
    header: t('inventory-mcol-date'),
    accessor: 'occurredAt',
    width: 170,
    cellRenderer: (value) => (
      <DateTimeDisplay startTime={value as string} showDateSeparately={false} dateFormat="MMM DD, YYYY" timeFormat="" />
    ),
  },
  {
    id: 'movementType',
    header: t('inventory-mcol-type'),
    accessor: 'movementType',
    width: 150,
    cellRenderer: (value) => {
      const type = value as InventoryMovementType;
      return <Tag color={getMovementTypeColor(type)}>{getMovementTypeLabel(type, t)}</Tag>;
    },
  },
  {
    id: 'quantity',
    header: t('inventory-mcol-quantity'),
    accessor: 'quantity',
    align: 'right',
    width: 110,
    cellRenderer: (value) => (
      <div className="flex w-full justify-end">
        <Typography className="tabular-nums">{value as number}</Typography>
      </div>
    ),
  },
  {
    id: 'price',
    header: t('inventory-mcol-price'),
    accessor: 'unitPrice',
    align: 'right',
    width: 150,
    cellRenderer: (_value, row) => (
      <div className="flex w-full justify-end">
        <Typography className="tabular-nums">{renderPrice(row, locale, t)}</Typography>
      </div>
    ),
  },
  {
    id: 'recipient',
    header: t('inventory-mcol-recipient'),
    accessor: 'recipientName',
    width: 200,
    cellRenderer: (_value, row) => <Typography>{renderRecipient(row, t)}</Typography>,
  },
  {
    id: 'charge',
    header: t('inventory-mcol-charge'),
    accessor: 'billingTransactionId',
    width: 130,
    cellRenderer: (value) =>
      value ? (
        <Tag color="purple" title={t('inventory-mcol-charge-posted')}>
          {`${t('inventory-mcol-charge')} #${value as number}`}
        </Tag>
      ) : (
        <Typography>-</Typography>
      ),
  },
  {
    id: 'notes',
    header: t('inventory-mcol-note'),
    accessor: 'notes',
    width: 260,
    cellRenderer: (value) => <Typography>{(value as string) || '-'}</Typography>,
  },
];
